import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Home, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ErrorFallbackProps {
    error: Error;
    reset: () => void;
}

export default function ErrorFallback({ error, reset }: ErrorFallbackProps) {
    const { t } = useTranslation();

    const handleGoHome = () => {
        window.location.href = '/';
    };

    const handleRefresh = () => {
        reset();
        window.location.reload();
    };

    const isDevelopment = import.meta.env.DEV;

    return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
            <Card className="w-full max-w-lg">
                <CardHeader>
                    <div className="flex items-center gap-2 text-destructive">
                        <AlertCircle className="size-6" />
                        <CardTitle>{t('error.title', 'Oops! Something went wrong')}</CardTitle>
                    </div>
                    <CardDescription>
                        {t(
                            'error.description',
                            "We're sorry, but something unexpected happened. Please try refreshing the page or go back to the homepage.",
                        )}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {isDevelopment && (
                        <div className="rounded-md bg-muted p-4">
                            <p className="mb-2 text-sm font-semibold text-destructive">{t('error.errorDetails', 'Error Details')}:</p>
                            <pre className="overflow-x-auto text-xs text-muted-foreground">
                                <code>{error.message}</code>
                            </pre>
                            {error.stack && (
                                <details className="mt-2">
                                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                                        {t('error.showStackTrace', 'Show stack trace')}
                                    </summary>
                                    <pre className="mt-2 overflow-x-auto text-xs text-muted-foreground">
                                        <code>{error.stack}</code>
                                    </pre>
                                </details>
                            )}
                        </div>
                    )}
                </CardContent>
                <CardFooter className="flex justify-between gap-2">
                    <Button onClick={handleRefresh} variant="default">
                        <RefreshCw className="size-4" />
                        {t('error.refresh', 'Refresh Page')}
                    </Button>
                    <Button onClick={handleGoHome} variant="outline">
                        <Home className="size-4" />
                        {t('error.goHome', 'Go to Homepage')}
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}
